import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { ALL_COUNTRIES, EU_COUNTRIES, PaymentMode, type MarketConfig } from '@etiquetachef/types';
import {
  subscribeMarketConfigs,
  type MarketConfigListResult,
} from '@/lib/firestoreTenantDetail';
import { MarketConfigEditDialog } from '@/features/billing/MarketConfigEditDialog';

type Row = {
  country: string;
  config: MarketConfig | null;
  isEu: boolean;
};

/**
 * Tela `/billing/markets` — mercado por país: se vende, por qual PSP (e em
 * produção ou sandbox), qual provedor fiscal e qual entidade vendedora.
 *
 * Origem: sem issue — commit 5af121d4c, 2026-05-25 (Sprint 35 PR G5, markets
 * CRUD).
 *
 * Contrato: assina `subscribeMarketConfigs` e monta uma linha por país de
 * `ALL_COUNTRIES`, depois os da UE que faltarem, depois qualquer doc avulso
 * — país sem doc aparece como bloqueado. A edição é do
 * `MarketConfigEditDialog` (callable no servidor).
 *
 * Usado por: rota `billing/markets` (`routes/router.tsx`), menu Sistema →
 * Mercados (`AdminLayout`, desde 2026-10-13 — até então só pela URL, #935
 * item 23). Spec: `docs/screens/billing-markets.md`.
 *
 * Dados: `config/markets/items/{country}` (`MarketConfig` de
 * `@etiquetachef/types`), rule `match /config/markets/items/{country}`.
 *
 * Arquitetura: mudar o mercado vale para vendas futuras; tenant ativo
 * mantém o que contratou (pricing congelado em `billing/subscription`). O
 * modo `SANDBOX` aparece na coluna porque país em sandbox não cobra cliente
 * real (#756; BR em Asaas sandbox é decisão do owner).
 *
 * Issues: #756 (modo do PSP por país).
 */
export function MarketsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<MarketConfigListResult>({ kind: 'LOADING' });
  const [editing, setEditing] = useState<Row | null>(null);

  useEffect(() => {
    setState({ kind: 'LOADING' });
    return subscribeMarketConfigs(setState);
  }, []);

  const rows: Row[] = useMemo(() => {
    const byCountry = new Map<string, MarketConfig>();
    if (state.kind === 'READY') {
      for (const m of state.markets) byCountry.set(m.country, m);
    }
    const seen = new Set<string>();
    const out: Row[] = [];
    for (const c of ALL_COUNTRIES) {
      out.push({ country: c, config: byCountry.get(c) ?? null, isEu: EU_COUNTRIES.includes(c) });
      seen.add(c);
    }
    for (const c of EU_COUNTRIES) {
      if (seen.has(c)) continue;
      out.push({ country: c, config: byCountry.get(c) ?? null, isEu: true });
      seen.add(c);
    }
    for (const [c, cfg] of byCountry) {
      if (seen.has(c)) continue;
      out.push({ country: c, config: cfg, isEu: EU_COUNTRIES.includes(c) });
    }
    return out;
  }, [state]);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('billing.markets.subtitle')}>
          {t('billing.markets.title')}
        </Header>
      }
    >
      {state.kind === 'LOADING' && (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      )}
      {state.kind === 'ERROR' && <Alert type="error">{t('billing.markets.errorLoad')}</Alert>}

      {state.kind === 'READY' && (
        <Table<Row>
          items={rows}
          trackBy="country"
          variant="container"
          header={<Header variant="h2">{t('billing.markets.tableTitle')}</Header>}
          footer={<Box color="text-status-inactive" fontSize="body-s">{t('billing.markets.frozenNotice')}</Box>}
          columnDefinitions={[
            {
              id: 'country',
              header: t('billing.markets.columns.country'),
              cell: (row): React.ReactNode => (
                <span>
                  <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.country}</span>
                  {row.isEu && (
                    <Box variant="span" color="text-status-inactive" fontSize="body-s">
                      {' '}UE
                    </Box>
                  )}
                </span>
              ),
            },
            {
              id: 'status',
              header: t('billing.markets.columns.status'),
              cell: (row): React.ReactNode =>
                row.config?.saleable ? (
                  <Badge color="green">{t('billing.markets.statusOpen')}</Badge>
                ) : (
                  <Badge color="grey">{t('billing.markets.statusBlocked')}</Badge>
                ),
            },
            {
              id: 'payment',
              header: t('billing.markets.columns.payment'),
              // #756 — o modo aparece junto: país em sandbox não cobra cliente real.
              cell: (row): React.ReactNode =>
                row.config?.payment_provider
                  ? row.config.payment_mode === PaymentMode.SANDBOX
                    ? `${row.config.payment_provider} (sandbox)`
                    : row.config.payment_provider
                  : '—',
            },
            {
              id: 'fiscal',
              header: t('billing.markets.columns.fiscal'),
              cell: (row): React.ReactNode => row.config?.fiscal_provider ?? '—',
            },
            // #958 (2026-10-13 11:23 +0200): sem coluna de entidade — o país não
            // aponta mais para PJ; ela sai do PSP (ver `MarketPspFields`).
            {
              id: 'actions',
              header: t('billing.markets.columns.actions'),
              cell: (row): React.ReactNode => (
                <Button
                  variant="inline-link"
                  onClick={(): void => {
                    setEditing(row);
                  }}
                >
                  {row.config?.saleable ? t('billing.markets.edit') : t('billing.markets.enable')}
                </Button>
              ),
            },
          ]}
        />
      )}

      <MarketConfigEditDialog
        open={editing !== null}
        country={editing?.country ?? null}
        current={editing?.config ?? null}
        onClose={(): void => {
          setEditing(null);
        }}
      />
    </ContentLayout>
  );
}
